import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useMemo, useState } from "react";
import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePathname, useSearchParams } from "next/navigation";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { ProfileDrawer } from "@/src/component/partial/ProfileDrawer";
import { QUERY_PARAMS } from "@/src/const";
import { ProviderHooks } from "@/src/hook";
import CMSLogic from "@/src/logic/CMSLogic";
import DynamicPageLogic from "@/src/logic/DynamicPageLogic";
import { StrapiCollectionType, StrapiComponentType, BrokerProfileType } from "@/src/type";
import { NextButtonPosition, NextIconColor } from "@/src/type/strapi/collection";

import styles from "./DynamicPage.module.scss";

import { TextModule } from "@/src/component/partial/__module__";

interface DynamicPageEffectsProps {
  page: StrapiCollectionType.DynamicPage;
  profile?: BrokerProfileType.BrokerProfile | null;
  searchParams?: Record<string, string | string[] | undefined>;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  appleOrMobileHint?: boolean;
  active: number;
  initialSlide?: number;
  next: string;
  profileDrawerActive?: number;
  swiper?: any;
  defaultNextButtonText?: string;
  defaultNextIconColor: NextIconColor;
  defaultNextButtonPosition?: NextButtonPosition;
}

type HandleImperativeDynamicPageEffectsRefType = {
  checkSection: (activeIndex?: number) => void;
};

type LegalDisclaimerStateType = {
  color: "primary" | "secondary";
  data?: StrapiComponentType.Element.LinkOptional;
  className?: string;
  timeout?: any;
};

const DynamicPageEffects: ForwardRefRenderFunction<HandleImperativeDynamicPageEffectsRefType, DynamicPageEffectsProps> = (
  {
    page,
    initialSlide,
    next,
    profileDrawerActive,
    swiper,
    searchParams,
    profile,
    defaultNextButtonText,
    defaultNextIconColor,
    defaultNextButtonPosition,
  }: DynamicPageEffectsProps,
  ref
) => {
  // hooks
  //const swiper = useSwiper();
  const pathname = usePathname();
  const router = useRouter();
  const params = useSearchParams();
  //const clientActive = params?.get("active");
  const app = ProviderHooks.useApp();
  const cms = ProviderHooks.useCMS();
  const currentActive = parseInt(params?.get(QUERY_PARAMS.ACTIVE) ?? "0", 10);

  //state
  const [nextButtonColor, setNextButtonColor] = useState<NextIconColor>(defaultNextIconColor ?? "light");
  const [nextButtonText, setNextButtonText] = useState<string>(defaultNextButtonText ?? "");
  const [nextButtonPosition, setNextButtonPosition] = useState<NextButtonPosition | "off">(defaultNextButtonPosition ?? "rightAndRight");
  const [legalDisclaimer, setLegalDisclaimer] = useState<LegalDisclaimerStateType | undefined>(undefined);

  const hasNudge = useMemo(() => page?.promo?.data?.attributes?.promoControl?.some((item) => item.name === "nudge"), [page]);

  /**
   * this prevents issues with SSR and window not being available
   */
  function getCurrentPathName() {
    return typeof window === "undefined" ? pathname : window.location.pathname;
  }

  /**
   * Next button position
   */
  function getNextButtonPositionStyle() {
    switch (nextButtonPosition) {
      case "center":
        return nextButtonText ? styles.centerWithText : styles.center;
      case "rightAndRight":
        return styles.right;
      case "off":
        return styles.off;
      default:
        return;
    }
  }

  /**
   * Legal disclaimer effect to add to the section
   */
  function checkLegalDisclaimer(section: any) {
    if (typeof window !== "undefined") {
      for (const tag of section?.attributes?.legalDisclaimerOff ?? []) {
        if (tag?.name && window.location.pathname.includes(tag.name)) {
          return;
        }
      }
    }

    if (legalDisclaimer) {
      if (legalDisclaimer?.timeout) {
        clearTimeout(legalDisclaimer?.timeout);
      }
      setLegalDisclaimer(undefined);
    }

    const timeout = setTimeout(() => {
      setLegalDisclaimer({
        color: section?.attributes?.nextIconColor === "dark" ? "primary" : "secondary",
        data: section?.attributes?.legalDisclaimer,
        className: styles.show,
      });
    }, 800);

    setLegalDisclaimer({
      color: section?.attributes?.nextIconColor === "dark" ? "primary" : "secondary",
      data: section?.attributes?.legalDisclaimer,
      timeout,
    });
  }

  /**
   * check the current section to determine the properties of the headers should change
   * this function will only update the header if the logo or shadow changes
   */
  function checkSection(activeIndex?: number) {
    const _activeIndex = activeIndex;
    if (typeof _activeIndex !== "number") {
      return;
    }

    if (legalDisclaimer) {
      if (legalDisclaimer?.timeout) {
        clearTimeout(legalDisclaimer?.timeout);
      }
      setLegalDisclaimer(undefined);
    }

    if (!Array.isArray(page.dynamic_page_sections?.data) || (page.dynamic_page_sections?.data ?? []).length === 0) {
      return;
    }

    //set header properties
    const currentSection = page.dynamic_page_sections?.data[_activeIndex];
    const backgroundColor = CMSLogic.colorSelector(currentSection?.attributes?.background?.color) ?? "transparent";
    app.setHeader({
      toChangeHeader: currentSection?.attributes?.header,
      toChangeBackgroundColor: backgroundColor,
      activeSlideIndex: _activeIndex,
      hasNudge,
    });

    //set the next icon color
    if (nextButtonColor !== getNextButtonColor(currentSection?.attributes)) {
      setNextButtonColor(getNextButtonColor(currentSection?.attributes));
    }

    if (nextButtonPosition !== getNextButtonPosition(currentSection?.attributes, page)) {
      setNextButtonPosition(getNextButtonPosition(currentSection?.attributes, page));
    }

    if (nextButtonText !== getNextButtonText(currentSection?.attributes)) {
      setNextButtonText(getNextButtonText(currentSection?.attributes));
    }

    if (currentSection?.attributes?.legalDisclaimer) {
      checkLegalDisclaimer(currentSection);
    }

    //set the active index as a query param
    const currentPathname = getCurrentPathName();
    if (typeof _activeIndex === "number" && currentPathname) {
      const currentParams = new URLSearchParams(params?.toString());
      if (_activeIndex > 0) {
        currentParams.set(QUERY_PARAMS.ACTIVE, `${_activeIndex}`);
        currentParams.delete(QUERY_PARAMS.SECTION_SLUG);
      } else {
        currentParams.delete(QUERY_PARAMS.ACTIVE);
      }
      const _currentParams = currentParams.toString();
      //alternate: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#using-the-native-history-api
      router.push(`${currentPathname}${_currentParams ? `?${_currentParams}` : ""}`, undefined, { shallow: true });
    }
  }

  useImperativeHandle(
    ref,
    (): HandleImperativeDynamicPageEffectsRefType => ({
      checkSection,
    })
  );

  /**
   * get the link for the legal disclaimer
   */
  function legalDisclaimerLink(legalDisclaimer: StrapiComponentType.Element.LinkOptional) {
    return legalDisclaimer?.isExternal ? legalDisclaimer?.optionalHref : cms.constructLink(legalDisclaimer?.optionalHref ?? "");
  }

  /*
  useEffect(() => {
    const swiperSlideChangeHandler = (e: any) => {
      checkSection(e?.data?.activeIndex);
    };

    window.addEventListener("swiperSlideChange", swiperSlideChangeHandler);
    return () => window.removeEventListener("swiperSlideChange", swiperSlideChangeHandler);
  }, [checkSection]);
  */

  /**
   * when the user clicks the back button scroll to the previous section
   * OR when user has click to an active index scroll to the section
   * OR when section slug scroll to active
   */
  useEffect(() => {
    //don't trigger until swipper is fully mounted
    if (!swiper || typeof swiper?.destroy === "boolean") {
      return;
    }

    const sectionSlug = params?.get(QUERY_PARAMS.SECTION_SLUG) ?? "";
    const __sectionSlugActive = DynamicPageLogic.exchangeSectionSlugForActiveId(page?.dynamic_page_sections, sectionSlug);
    const __clientActive = __sectionSlugActive ?? parseInt(`${currentActive}`, 10);
    if (typeof __clientActive === "number" && typeof swiper?.activeIndex === "number" && __clientActive !== swiper?.activeIndex) {
      swiper.slideTo(__clientActive);
    }
  }, [currentActive, swiper, params, page]);

  /**
   * on initial load set the next section color
   * on intial load check the lead disclaimer
   */
  useEffect(() => {
    const __initialSlide = initialSlide ?? 0;
    if (typeof __initialSlide !== "number") {
      return;
    }

    if (!Array.isArray(page.dynamic_page_sections?.data) || (page.dynamic_page_sections?.data ?? []).length === 0) {
      return;
    }

    checkSection(__initialSlide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlide, page]);

  return (
    <>
      <div className={cn(styles.nextButton, getNextButtonPositionStyle())}>
        <IconButton
          id={next}
          className={styles.iconButton}
          aria-label="Next Section"
        >
          {(currentActive ?? 0) + 1 < page.dynamic_page_sections.data.length && (
            <span>
              {nextButtonText && (
                <TextModule
                  text={{ text: nextButtonText ?? "", tag: "body1", color: nextButtonColor === "dark" ? "primaryMain" : "secondaryMain" }}
                  className={styles.nextButtonText}
                />
              )}
              <Image
                src={getNextIconColor(nextButtonColor)}
                width={40}
                height={40}
                alt="Next Card"
                className={styles.next}
              />
            </span>
          )}
        </IconButton>
      </div>
      <ProfileDrawer
        profile={profile}
        searchParams={searchParams}
        promo={page.promo_profile_widget?.data?.attributes}
      />
      {legalDisclaimer?.data?.label && (
        <div
          className={cn(
            styles.legalDisclaimer,
            legalDisclaimer.className,
            legalDisclaimer?.color === "primary" ? styles.primary : styles.secondary,
            profileDrawerActive ? styles.moveUp : undefined,
            profileDrawerActive ? styles.twoLines : undefined
          )}
        >
          {legalDisclaimer?.data?.optionalHref ? (
            <Link
              href={legalDisclaimerLink(legalDisclaimer?.data) ?? ""}
              target={legalDisclaimer?.data?.target}
            >
              <Typography variant="body1">{legalDisclaimer?.data?.label}</Typography>
            </Link>
          ) : (
            <Typography variant="body1">{legalDisclaimer?.data?.label}</Typography>
          )}
        </div>
      )}
    </>
  );
};

/**
 * when on a light background allow the user to
 */
function getNextIconColor(nextIconColor: NextIconColor): string {
  return nextIconColor === "dark" ? "/image/icon_arrow_down_thick_dark.svg" : "/image/icon_arrow_down_thick.svg";
}

function getNextButtonText(currentSection: StrapiCollectionType.DynamicPageSection<any>): string {
  const nextButtonText = currentSection?.nextButtonText;
  if (!nextButtonText) {
    return "";
  }
  return nextButtonText;
}

function getNextButtonColor(currentSection: StrapiCollectionType.DynamicPageSection<any>): "light" | "dark" {
  const nextButtonColor = currentSection?.nextIconColor;
  if (!nextButtonColor) {
    return "light";
  }
  return nextButtonColor;
}

function getNextButtonPosition(
  currentSection: StrapiCollectionType.DynamicPageSection<any>,
  page: StrapiCollectionType.DynamicPage
): NextButtonPosition | "off" {
  const sectionNextButtonPosition = currentSection?.nextButtonPosition;
  if (sectionNextButtonPosition) {
    return sectionNextButtonPosition;
  }

  const pageNextButtonPosition = page?.nextButtonPosition;
  if (pageNextButtonPosition) {
    return pageNextButtonPosition;
  }

  return "rightAndCenter";
}

export default forwardRef(DynamicPageEffects);
